import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Snackbar, Alert, TextField, Pagination,
  Stack, Tooltip, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { deletePixIntegration, getAllPixIntegrations } from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormPixIntegration from './FormPixIntegration';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { sxLinhaInativa } from './common/StatusAtivo';
import { getApiErrorMessage } from '../utils/apiError';

const PSP_LABELS = {
  0: 'Bradesco',
  1: 'Itaú',
  2: 'Banco do Brasil',
  3: 'Santander',
  4: 'Shipay',
  5: 'Sicredi',
  6: 'Sicoob',
  7: 'PagSeguro',
  8: 'Gerencianet',
  9: 'PixPDV',
  10: 'Inter',
  11: 'Ailos',
  12: 'Matera',
  13: 'Cielo',
  14: 'MercadoPago',
  18: 'AppLess',
};

export default function PixIntegrationList() {
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina] = useState(20);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [snack, setSnack] = useState({ open: false, sev: 'success', msg: '' });
  const [form, setForm] = useState({ open: false, id: null });

  const carregar = useCallback(async () => {
    try {
      const { data } = await getAllPixIntegrations();
      setItems(Array.isArray(data) ? data : []);
      setPagina(1);
    } catch {
      setSnack({ open: true, sev: 'error', msg: 'Erro ao carregar integrações PIX' });
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnack({ open: true, sev: 'success', msg });
    carregar();
  };

  const filtrados = useMemo(() => {
    const t = termo.trim().toLowerCase();
    if (!t) return items;
    return items.filter((p) =>
      (p.descricao || '').toLowerCase().includes(t) ||
      (PSP_LABELS[p.psp] || '').toLowerCase().includes(t) ||
      (p.chavePix || '').toLowerCase().includes(t)
    );
  }, [items, termo]);

  const totalItens = filtrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalItens / itensPorPagina));
  const pixAccessors = useMemo(() => ({
    psp: (p) => PSP_LABELS[p.psp] || `PSP ${p.psp}`,
    status: (p) => (p.ativo ? 1 : 0),
  }), []);
  const { sorted: ordenados, orderBy, order, requestSort } = useClientSort(
    filtrados,
    'descricao',
    'asc',
    pixAccessors,
  );
  const paginaOrdenada = ordenados.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina);
  const paginaAtual = paginaOrdenada;

  const excluir = async () => {
    try {
      await deletePixIntegration(confirmId);
      setSnack({ open: true, sev: 'success', msg: 'Integração PIX excluída' });
      setConfirmId(null);
      await carregar();
    } catch (e) {
      setConfirmId(null);
      setSnack({
        open: true,
        sev: 'error',
        msg: getApiErrorMessage(e, 'Não foi possível excluir a integração PIX.'),
      });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Integrações PIX"
        subtitle="Credenciais PSP usadas para receber PIX no PDV."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
            Nova integração
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar por descrição, PSP ou chave..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setTermo(searchText)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: !!searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}><CloseIcon fontSize="small" /></IconButton>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: 260 }}
        />
        <Button variant="outlined" onClick={() => setTermo(searchText)}>Buscar</Button>
      </Paper>

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="ID" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="psp" label="PSP" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="ambiente" label="Ambiente" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="chavePix" label="Chave" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginaAtual.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma integração PIX encontrada
                </TableCell>
              </TableRow>
            ) : paginaOrdenada.map((p) => (
              <TableRow key={p.id} hover sx={{ cursor: 'pointer', ...sxLinhaInativa(p.ativo) }} onClick={() => abrirForm(p.id)}>
                <TableCell data-label="ID">{p.id}</TableCell>
                <TableCell data-label="Descrição">{p.descricao}</TableCell>
                <TableCell data-label="PSP">{PSP_LABELS[p.psp] || `PSP ${p.psp}`}</TableCell>
                <TableCell data-label="Ambiente">
                  <Chip
                    size="small"
                    label={Number(p.ambiente) === 1 ? 'Produção' : 'Homologação'}
                    color={Number(p.ambiente) === 1 ? 'warning' : 'info'}
                  />
                </TableCell>
                <TableCell data-label="Chave" sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.chavePix || '—'}
                </TableCell>
                <TableCell data-label="Status">
                  <Chip size="small" label={p.ativo ? 'Ativo' : 'Inativo'} color={p.ativo ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => abrirForm(p.id)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton color="error" onClick={() => setConfirmId(p.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {paginaAtual.length} de {totalItens} (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar integração Pix' : 'Nova integração Pix'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="md"
      >
        <FormPixIntegration
          key={form.id || 'novo'}
          embedded
          pixIntegrationId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      <Dialog open={!!confirmId} onClose={() => setConfirmId(null)}>
        <DialogTitle>Excluir Integração PIX</DialogTitle>
        <DialogContent><DialogContentText>Confirmar exclusão?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>Cancelar</Button>
          <Button color="error" onClick={excluir}>Excluir</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
